import {Locale} from '../../RootProvider/LocaleContext';
import {ObjectOfAny, ObjectOfStrings} from '../../types';
import {formatObjectToQueryString, replaceParamFragments} from './formatting';
import {getRoutesData} from './getRoutesData';
import {isBrowser} from '../../browserSupport';
import {QuoteFormStep} from './types';

interface RouteParams {
  slugs: ObjectOfStrings;
  params: ObjectOfStrings;
  fragments?: ObjectOfStrings;
}

export const getRouteFromTemplate = (
  stringKey: string,
  routes: ObjectOfAny = {},
  {slugs, params, fragments = {}}: RouteParams
): string | undefined => {
  const keys = stringKey
    .replace(/\[(\w+)\]/g, '.$1') // convert indexes to properties
    .replace(/^\./, '') // strip a leading dot
    .split('.'); // split into array

  // get template string from deep key in localised routes config
  const template: unknown = keys.reduce((val, key) => {
    // bail out for undefined values
    if (!val) {
      return undefined;
    }
    // drill deeper
    if (key in val) {
      return val[key];
    }
    return undefined; // missing key so return undefined
  }, routes);

  // when a template string is found replaces slug, param and other fragments in the template with data from the args
  if (typeof template === 'string') {
    const slugsReplaced = replaceParamFragments(template, slugs, 'slugs');
    const paramsReplaced = replaceParamFragments(slugsReplaced, params, 'params').trim();
    const othersReplaced = replaceParamFragments(paramsReplaced, fragments);
    return othersReplaced;
  }

  // return undefined if no template found
  return undefined;
};

export const getPageRoute = (
  routeKey: string,
  locale: Locale,
  params: ObjectOfStrings = {},
  queryParams: ObjectOfStrings = {}
): string => {
  try {
    // get config data from localised config files
    const data = getRoutesData(locale);
    // @todo: use optional chaining once gatsby-node supports transpilation https://github.com/gatsbyjs/gatsby/issues/23802
    const slugs: ObjectOfStrings = (data && data.slugs) || {};
    const routes = (data && data.routes) || {};
    const localePathPrefix = (data && data.localePath) || '';
    const translatedRoute = getRouteFromTemplate(routeKey, routes, {slugs, params});

    if (!translatedRoute) {
      throw new Error('Could not find route in config');
    }

    // relative urls should have the localePath prefixed
    const routeForLocale = `${localePathPrefix}${!translatedRoute.startsWith('/') ? '/' : ''}${translatedRoute}`;
    // we also need to format and append any querystrings
    const queryString = formatObjectToQueryString(queryParams);
    const finalRoute = `${routeForLocale}${queryString}`;

    return finalRoute;
  } catch (err) {
    const errMessage = `Error formatting route for key "${routeKey}": ${(err as Error).message}`;

    // log sentry error
    if (isBrowser && window.Sentry) {
      window.Sentry.addBreadcrumb({
        message: errMessage,
        category: 'page_route_formatter',
        data: {
          routeKey,
          locale,
          params,
          queryParams,
        },
      });
      window.Sentry.captureException(err);
    }

    throw new Error(errMessage);
  }
};

export const getQuoteFormPageRoute = (
  nextStep: QuoteFormStep,
  quoteFormBaseUrlWithLocale: string,
  locale: Locale,
  applicationId: string
): string => {
  const {slugs} = getRoutesData(locale);
  const nextStepRoute = slugs[nextStep];
  return `${quoteFormBaseUrlWithLocale}/${applicationId}/${nextStepRoute}`;
};
